import { Component, Input, OnDestroy } from '@angular/core';
import { IConfirmModalConfig } from '@core';
import { Unsubscriber } from '@core-lib/classes';
import { AuthSharedService } from '@core-lib/services';
import { ModalRef } from '@core-lib/modules/modal-container';
import { interval, Subscription } from 'rxjs';

@Component({
    selector: 'confirmation-edition-modal',
    templateUrl: './confirmation-edition-modal.component.html',
    standalone: false
})
export class ConfirmationEditionModalComponent extends Unsubscriber implements OnDestroy {
	private _downloadToken: string;
	expirationDate: Date;
	remainingSeconds: number = 0;
	timerSubscription: Subscription;
	tokenExpired = false;

	@Input() confirmAction: () => void;

	@Input()
	set downloadToken(value: string) {
		if (value) {
			this._downloadToken = value;
			this.initializeTokenExpiration();
		}
	}

	get downloadToken(): string {
		return this._downloadToken;
	}

	private _config: IConfirmModalConfig = {
		textsKey: 'EditionConfirm',
		confirmBtnText: 'Actions.Ok',
		cancelBtnText: 'Actions.Close',
		canClose: false,
		canConfirm: true,
		modalOptions: { backdrop: 'static' }
	};

	isConfigLoaded: boolean = false;

	@Input() set config(config: IConfirmModalConfig) {
		if (config) {
			this._config = {
				confirmBtnText: 'Actions.Ok',
				cancelBtnText: 'Actions.Close',
				canClose: false,
				canConfirm: true,
				...config
			};
			this.isConfigLoaded = true;
		} else {
			this.isConfigLoaded = false;
		}
	}

	get config(): IConfirmModalConfig {
		return this._config;
	}

	constructor(
		private modalRef: ModalRef,
		private authService: AuthSharedService
	) {
		super();
	}

	initializeTokenExpiration(): void {
		const decodedToken = this.authService.decodeJwtToken(this._downloadToken);

		if (!decodedToken || !decodedToken.exp) {
			this.tokenExpired = true;
			return;
		}

		this.expirationDate = new Date(+decodedToken.exp * 1000);
		this.updateRemainingSeconds();

		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}

		this.timerSubscription = interval(1000).subscribe(() => {
			this.updateRemainingSeconds();
			if (this.remainingSeconds <= 0) {
				this.tokenExpired = true;
				this.timerSubscription.unsubscribe();
			}
		});
	}

	updateRemainingSeconds(): void {
		const now = new Date();
		this.remainingSeconds = Math.max(Math.floor((this.expirationDate.getTime() - now.getTime()) / 1000), 0);
	}

	onConfirm(): void {
		if (!this.tokenExpired && this.confirmAction) {
			this.confirmAction();
			this.modalRef.hide();
		}
	}

	onClose(): void {
		this.modalRef.hide();
	}

	ngOnDestroy(): void {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}
	}
}
